/**
 * Paging, searching and sorting a list of everything on the instance.
 *
 * The accounts and the workspaces are the same endpoint twice: same query
 * parameters, same envelope, same rules about what may be sorted. All of that
 * is here, pure, so the two pages differ only in their columns.
 */

/** How many rows a page holds. The endpoint refuses more than a hundred. */
export const PAGE_SIZE = 50

export type SortDirection = 'asc' | 'desc'

export interface Listing {
  page: number
  search: string
  /** One field at a time, as `+field` or `-field`, or empty for the default. */
  sort: string
}

/**
 * Which way a column is sorted, if it is.
 *
 * `+` is ascending and `-` is descending, which is the *opposite* of what the
 * endpoint's own documentation says. The parser is unambiguous -- `+` maps to no
 * Django prefix and `-` to `-` -- so the description is wrong rather than the
 * behaviour.
 */
export function sortDirection(sort: string, field: string): SortDirection | null {
  if (sort.slice(1) !== field) return null
  if (sort.startsWith('+')) return 'asc'
  if (sort.startsWith('-')) return 'desc'
  return null
}

/**
 * What clicking a column heading does next.
 *
 * Ascending, then descending, then no sort at all -- so a heading clicked by
 * mistake can be put back rather than leaving the list ordered by something
 * nobody chose. Clicking a different column starts that column afresh.
 */
export function toggledSort(sort: string, field: string): string {
  const held = sortDirection(sort, field)
  if (held === null) return `+${field}`
  return held === 'asc' ? `-${field}` : ''
}

/** How many pages there are, and never fewer than one to be looking at. */
export function pageCount(count: number, size = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(count / size))
}

/** The query string a listing asks for, with the parts it does not need left out. */
export function listingQuery(listing: Listing): Record<string, string | number> {
  return {
    page: listing.page,
    size: PAGE_SIZE,
    ...(listing.search === '' ? {} : { search: listing.search }),
    ...(listing.sort === '' ? {} : { sorts: listing.sort }),
  }
}
